// Vite environment variable access
// Support both VITE_* (new) and REACT_APP_* (fallback for transition)
// Both prefixes are exposed via vite.config.ts envPrefix configuration
const getEnvVar = (viteKey: string, reactKey?: string): string | undefined => {
  // Try VITE_ prefix first
  const viteValue = import.meta.env[viteKey];
  if (viteValue !== undefined && viteValue !== '') return viteValue;

  // Fallback to REACT_APP_ prefix (also exposed via import.meta.env due to envPrefix config)
  if (reactKey) {
    const reactValue = import.meta.env[reactKey];
    if (reactValue !== undefined && reactValue !== '') return reactValue;
  }

  return undefined;
};

/**
 * App Environment Configuration
 *
 * VITE_APP_ENV is a two-value switch that controls which backend to use:
 * - 'development' → staging backend (Firebase dev project, staging Functions)
 * - 'production' → production backend (Firebase prod project, production Functions)
 *
 * This is separate from Vite's MODE (--mode development|production):
 * - Vite MODE = which .env file to load (.env.development vs .env.production)
 * - VITE_APP_ENV = which backend config to use (staging vs production)
 *
 * Note: If you later need a third state (e.g., 'staging' as distinct from 'development'),
 * update this logic to use a 3-state enum instead of boolean isProduction.
 */
const appEnv = import.meta.env.VITE_APP_ENV || import.meta.env.REACT_APP_ENV || 'development'; // Default to development (staging) if not set

// Validate appEnv value
if (appEnv !== 'development' && appEnv !== 'production') {
  throw new Error(`Invalid VITE_APP_ENV value: "${appEnv}". Must be either 'development' or 'production'.`);
}

const isProduction = appEnv === 'production';

// Validate required Firebase config in development mode (fail fast)
const validateFirebaseConfig = () => {
  if (import.meta.env.DEV) {
    // In dev mode, validate critical Firebase config
    const requiredVars = isProduction
      ? [
          { key: 'VITE_FIREBASE_API_KEY', name: 'Firebase API Key' },
          { key: 'VITE_FIREBASE_PROJECT_ID', name: 'Firebase Project ID' },
          { key: 'VITE_FUNCTIONS_BASE_URL', name: 'Functions Base URL' }
        ]
      : [
          { key: 'VITE_STAGING_FIREBASE_API_KEY', name: 'Staging Firebase API Key' },
          { key: 'VITE_STAGING_FIREBASE_PROJECT_ID', name: 'Staging Firebase Project ID' },
          { key: 'VITE_STAGING_FUNCTIONS_BASE_URL', name: 'Staging Functions Base URL' }
        ];

    const missing = requiredVars.filter(({ key }) => !getEnvVar(key));
    if (missing.length > 0) {
      console.warn(
        '⚠️ Missing Firebase configuration variables:\n' +
          missing.map(({ name, key }) => `   - ${name} (${key})`).join('\n') +
          '\n   The app may not function correctly. Please check your .env file.'
      );
    }
  }
};

// Run validation
validateFirebaseConfig();

// Validate critical vars in production builds (fail fast)
const validateCriticalVars = () => {
  if (import.meta.env.PROD) {
    // In production builds, enforce required vars
    const criticalVars = isProduction
      ? [
          { viteKey: 'VITE_FIREBASE_API_KEY', reactKey: 'REACT_APP_FIREBASE_API_KEY', name: 'Firebase API Key' },
          {
            viteKey: 'VITE_FIREBASE_PROJECT_ID',
            reactKey: 'REACT_APP_FIREBASE_PROJECT_ID',
            name: 'Firebase Project ID'
          },
          { viteKey: 'VITE_FUNCTIONS_BASE_URL', reactKey: 'REACT_APP_FUNCTIONS_BASE_URL', name: 'Functions Base URL' }
        ]
      : [
          {
            viteKey: 'VITE_STAGING_FIREBASE_API_KEY',
            reactKey: 'REACT_APP_STAGING_FIREBASE_API_KEY',
            name: 'Staging Firebase API Key'
          },
          {
            viteKey: 'VITE_STAGING_FIREBASE_PROJECT_ID',
            reactKey: 'REACT_APP_STAGING_FIREBASE_PROJECT_ID',
            name: 'Staging Firebase Project ID'
          },
          {
            viteKey: 'VITE_STAGING_FUNCTIONS_BASE_URL',
            reactKey: 'REACT_APP_STAGING_FUNCTIONS_BASE_URL',
            name: 'Staging Functions Base URL'
          }
        ];

    // Check all critical vars (but don't throw in config.ts - let Firebase.ts handle it)
    // This is just for early detection
    const missing = criticalVars.filter(({ viteKey, reactKey }) => !getEnvVar(viteKey, reactKey));
    if (missing.length > 0) {
      console.error(
        '❌ Missing critical environment variables in production build:\n' +
          missing.map(({ name }) => `   - ${name}`).join('\n') +
          '\n   The app will fail at runtime. Please set these variables before building.'
      );
    }
  }
};

// Run validation for production builds
validateCriticalVars();

const ENV_VARIABLES = {
  apiKey: isProduction
    ? getEnvVar('VITE_FIREBASE_API_KEY', 'REACT_APP_FIREBASE_API_KEY')
    : getEnvVar('VITE_STAGING_FIREBASE_API_KEY', 'REACT_APP_STAGING_FIREBASE_API_KEY'),
  //________________
  authDomain: isProduction
    ? getEnvVar('VITE_FIREBASE_AUTH_DOMAIN', 'REACT_APP_FIREBASE_AUTH_DOMAIN')
    : getEnvVar('VITE_STAGING_FIREBASE_AUTH_DOMAIN', 'REACT_APP_STAGING_FIREBASE_AUTH_DOMAIN'),
  //________________
  projectId: isProduction
    ? getEnvVar('VITE_FIREBASE_PROJECT_ID', 'REACT_APP_FIREBASE_PROJECT_ID')
    : getEnvVar('VITE_STAGING_FIREBASE_PROJECT_ID', 'REACT_APP_STAGING_FIREBASE_PROJECT_ID'),
  //________________
  storageBucket: isProduction
    ? getEnvVar('VITE_FIREBASE_STORAGE_BUCKET', 'REACT_APP_FIREBASE_STORAGE_BUCKET')
    : getEnvVar('VITE_STAGING_FIREBASE_STORAGE_BUCKET', 'REACT_APP_STAGING_FIREBASE_STORAGE_BUCKET'),
  //________________
  messagingSenderId: isProduction
    ? getEnvVar('VITE_FIREBASE_MESSAGING_SENDER_ID', 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID')
    : getEnvVar('VITE_STAGING_FIREBASE_MESSAGING_SENDER_ID', 'REACT_APP_STAGING_FIREBASE_MESSAGING_SENDER_ID'),
  //________________
  appId: isProduction
    ? getEnvVar('VITE_FIREBASE_APP_ID', 'REACT_APP_FIREBASE_APP_ID')
    : getEnvVar('VITE_STAGING_FIREBASE_APP_ID', 'REACT_APP_STAGING_FIREBASE_APP_ID'),
  //________________
  userName: isProduction
    ? getEnvVar('VITE_USERNAME', 'REACT_APP_USERNAME')
    : getEnvVar('VITE_STAGING_USERNAME', 'REACT_APP_STAGING_USERNAME'),
  //________________
  googleMapsAPIKey: isProduction
    ? getEnvVar('VITE_GOOGLE_MAPS_API_KEY', 'REACT_APP_GOOGLE_MAPS_API_KEY')
    : getEnvVar('VITE_STAGING_GOOGLE_MAPS_API_KEY', 'REACT_APP_STAGING_GOOGLE_MAPS_API_KEY'),
  //________________
  // reCAPTCHA Site Key (public key - safe to expose in frontend)
  // Note: Secret key should NEVER be in frontend code - it belongs in backend/Functions only
  // IMPORTANT: Casing must match exactly - uses lowercase "re": VITE_reCAPTCHA_SITE_KEY (not VITE_RECAPTCHA_SITE_KEY)
  reCAPTCHASiteKey: isProduction
    ? getEnvVar('VITE_reCAPTCHA_SITE_KEY', 'REACT_APP_reCAPTCHA_SITE_KEY')
    : getEnvVar('VITE_STAGING_reCAPTCHA_SITE_KEY', 'REACT_APP_STAGING_reCAPTCHA_SITE_KEY'),
  //________________
  functionsBaseUrl: isProduction
    ? getEnvVar('VITE_FUNCTIONS_BASE_URL', 'REACT_APP_FUNCTIONS_BASE_URL')
    : getEnvVar('VITE_STAGING_FUNCTIONS_BASE_URL', 'REACT_APP_STAGING_FUNCTIONS_BASE_URL'),
  //________________
  breakpoint: parseInt(getEnvVar('VITE_BREAKPOINT', 'REACT_APP_BREAKPOINT') || '750')
};

export default ENV_VARIABLES;
